import { WireStatus, useWire } from '@egw/api'
import { Button, ButtonVariant, Link, ModalDrawer, PageHeader } from '@egw/components'
import { configClient } from '@egw/config'
import { DEFAULT_CURRENCY_CODE, IMMEDIATELY, WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { Trans, useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { useRouter } from '@egw/router'
import { Currency } from '@egw/utils'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import { ReviewContentSection, TransferReviewSection } from '../../../components'
import { ClientServiceError } from '../../../components/client-service-error/client-service-error'
import { type Config } from '../../../config/config'
import { useSubmitWire } from '../../../hooks'
import { breakdownWireForReviewPage } from '../../../utils'

const log = new Logger('Wire Review Page')

export const WireReviewPage: FC = () => {
  const { t } = useTranslation('features-transfer')

  const { navigate, searchParams } = useRouter()

  const id = searchParams().get('id') as string

  // Bounce users without a wire id in params
  useEffect(() => {
    if (!id) navigate(WEALTH_ROUTES.TRANSFER_WIRE.path)
  }, [id, navigate])

  const [wireError, setWireError] = useState<boolean>(false)

  const onError = useCallback(() => {
    log.error('Wire Transfer Submitted Unsuccessfully; Setting Wire Error')
    setWireError(true)
  }, [])

  const { data: dataRes, isFetched } = useWire(id, {
    enabled: Boolean(id),
    // IMMEDIATE STALETIME ENSURES WE ALWAYS FETCH FOR THE WIRE ENSURING NEWLY SAVED WIRE DATA IS AVAILABLE
    staleTime: IMMEDIATELY,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })
  const data = useMemo(() => {
    if (dataRes) {
      // IF WIRE HAS NOT BEEN SUBMITTED POPULATE THE WIRE REVIEW PAGE
      return {
        wireData: dataRes,
        ...breakdownWireForReviewPage(dataRes),
      }
    }
    return undefined
  }, [dataRes])

  useEffect(() => {
    // IF WIRE HAS BEEN SUBMITTED ALREADY GO TO WIRE TRANSFER PAGE
    if (dataRes?.isSubmitted) {
      navigate(WEALTH_ROUTES.TRANSFER_WIRE.path)
    }
  }, [dataRes, navigate])

  const onSuccess = (): void => {
    log.info('Wire Transfer Submitted Successfully; Navigating to Success Page')
    navigate(WEALTH_ROUTES.TRANSFER_WIRE_SUCCESS.path)
  }

  const { submitWire, wireSubmissionIsLoading } = useSubmitWire({ onSuccess, onError })

  const sendWireHandler = (): void => {
    log.info('Submitting Wire Transfer')
    sendEvent('user_wireTranser_submit', {
      eventSource: 'egWealth',
      category: 'wire-transfer',
      subCategory: 'intent',
      // Product Name not relevant here as this is the old wire flow, the only way to see this would be is for Multi FF to be off
      fromAccount: 'High Yield Checking',
      accountType: 'checking',
      recipient: data?.recipient,
      memo: data?.wireData.reference,
      amount: data?.amount,
    })

    data?.wireData && submitWire(data.wireData)
  }

  useEffect(() => {
    // If Wire Error has been set launch Client Services Modal
    if (wireError) return
    // If Wire cannot be found show setError
    // and change the page to no longer have the wire id
    if (isFetched && data === undefined) {
      onError()
      navigate(WEALTH_ROUTES.TRANSFER_WIRE.path)
    }
  }, [wireError, onError, data, isFetched, navigate])

  return (
    <>
      <PageHeader
        testId="wire-tranfer-review-page"
        backLinkUrl={WEALTH_ROUTES.TRANSFER_WIRE.path}
        title={t('wire_transfer_review.title')}
        subtitle={t('wire_transfer_review.subtitle')}
      />
      <div className="egw-container">
        <div className="flex flex-col gap-4">
          <TransferReviewSection
            sectionTitle={t('wire_transfer_review.from')}
            // Product Name not relevant here as this is the old wire flow, the only way to see this would be is for Multi FF to be off
            sectionContentPrimary={t('wire_transfer_review.high_yield_checking')}
          />
          <TransferReviewSection
            sectionTitle={t('wire_transfer_review.amount')}
            sectionContentPrimary={
              <p>
                {t(`currency.${DEFAULT_CURRENCY_CODE}`, {
                  amount: Currency.integerCents.omitSign(data?.amount ?? 0),
                })}
              </p>
            }
          />
          <TransferReviewSection
            sectionTitle={t('wire_transfer_review.recipient')}
            sectionContentPrimary={
              <ReviewContentSection
                sectionContentPrimary={data?.recipient}
                sectionContentSecondary={[
                  t('wire_transfer_review.routing', {
                    routingNumber: data?.beneficiaryRoutingNumber,
                  }),
                  t('wire_transfer_review.account', {
                    accountNumber: data?.beneficiaryAccountNumber,
                  }),
                ]}
              />
            }
            sectionContentSecondary={
              data?.intermediaryRoutingNumber ? (
                <ReviewContentSection
                  className="mt-4"
                  sectionContentPrimary={t('wire_transfer_review.intermediary_bank')}
                  sectionContentSecondary={[
                    t('wire_transfer_review.routing', {
                      routingNumber: data.intermediaryRoutingNumber,
                    }),
                  ]}
                />
              ) : null
            }
          />
          <div className="flex flex-col text-meta gap-4">
            <p className="text-base">{t('wire_transfer_review.reference')}</p>
            <p className="text-sm">{data?.reference}</p>
          </div>
        </div>
        {data?.isSubmitted === false && data.status === WireStatus.PENDING ? (
          <WireReviewPageFooter
            wireId={data.id}
            sendWireHandler={sendWireHandler}
            isLoading={wireSubmissionIsLoading}
          />
        ) : null}

        {/* Error Modal */}
        <ModalDrawer
          title={t('error.modal_title')}
          isOpen={wireError}
          onClose={() => {
            setWireError(false)
          }}
        >
          <ClientServiceError />
        </ModalDrawer>
      </div>
    </>
  )
}

export default WireReviewPage

interface TWireReviewPageFooter {
  wireId: string
  sendWireHandler: () => void
  isLoading: boolean
}

const WireReviewPageFooter: FC<TWireReviewPageFooter> = ({ wireId, sendWireHandler, isLoading }) => {
  const { t } = useTranslation('features-transfer')
  const config = configClient.getConfig() as Config
  const { navigate } = useRouter()

  return (
    <>
      <div className="flex flex-col gap-10 mb-4 pt-4">
        {/* <AlertInfo content={t('wire_transfer_review.info_alert')} /> TODO: Implement AlertInfo component */}
        <p>{t('wire_transfer_review.info_alert')}</p>
        <span className="text-meta text-sm">
          <Trans t={t} i18nKey="wire_transfer_review.wire_transfer_agreement">
            <Link href={config.zendeskArticles.wireTransferAgreement} className="hyperlink-muted inline-flex" />
          </Trans>
        </span>
      </div>
      <div className="flex max-md:flex-col md:flex-row gap-4">
        <Button variant={ButtonVariant.Primary} id="sendWireTransfer" onClick={sendWireHandler} isLoading={isLoading}>
          {t('wire_transfer_review.send_wire_transfer')}
        </Button>
        <Button
          variant={ButtonVariant.Secondary}
          id="makeChanges"
          onClick={() => {
            navigate(`${WEALTH_ROUTES.TRANSFER_WIRE.path}?id=${wireId}`)
          }}
        >
          {t('wire_transfer_review.make_changes')}
        </Button>
      </div>
    </>
  )
}
